import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import usePageTitle from "hooks/usePageTitle";
import moment from "moment";
import Icon from "components/blocks/Icon/Icon";
import useGetCenter from "services/company/useGetCenter";
import MapGoogle2 from "components/blocks/MapGoogle/MapGoogle2";
import PageMenu from "components/blocks/PageMenu/PageMenu";
import "./CenterDetailNew.scss";
import { isHospitalCenter, isOwnCenter, SortableCenter } from "@/utils/centerSorting";

const CenterDetailNew: React.FC = () => {
  const { id: centroId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  usePageTitle("Detalle de Centro");
  const [center, setCenter] = useState<any>({});
  const [isHospital, setIsHospital] = useState<boolean>(false);
  const [own, setIsOwn] = useState<boolean>(false);
  const { getCenter, loadingGetCenter } = useGetCenter();

  const redirect = (latitud: number, longitud: number) => {
    window.open(`https://maps.google.com/?q=${latitud},${longitud}`);
  };

  useEffect(() => {
    handleGetCenter();
  }, []);
 
 
  const handleGetCenter = async () => {
    const data = await getCenter(centroId);
    if (data?.Horarios) {
      data.Horarios.sort((a: any, b: any) => a.TipoHorario - b.TipoHorario);
    }
    setCenter(data);
    setIsHospital(isHospitalCenter(data as SortableCenter));
    setIsOwn(isOwnCenter(data.EsCentroPropio));
  };

  const addr = center.TipoViaDescripcion
    ? `${center.TipoViaDescripcion} ${center.NombreCalle} ${center.NumeroCalle}, ${center.CodigoPostal}, ${center.LocalidadDescripcion}`
    : "";

  return (
    <div>
      <PageMenu
        title={center.Descripcion}
        subtitle={
          center.LocalidadDescripcion
            ? `Su centro de salud en ${center.LocalidadDescripcion}`
            : ""
        }
        breadcrumbs={["Red de centros", center.Descripcion || ""]}
      >
        <div className="btns-header-container">
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => navigate(-1)}
          >
            <Icon name="arrow-left" size={16} /> Volver
          </button>
        </div>
      </PageMenu>

      <section className="section">
        <div className="container">
          {center.ObservacionesPublicas &&
            center.ObservacionesPublicas.map((obs: any, i: number) => {
              const fechaPublicacion = moment(obs.FechaPublicacion);
              const fechaCaducidad = obs.FechaCaducidad
                ? moment(obs.FechaCaducidad)
                : null;
              const now = moment();
              if (
                !fechaPublicacion.isSameOrBefore(now) ||
                (fechaCaducidad && fechaCaducidad.isBefore(now))
              ) {
                return null;
              }
              return (
                <div className="cd-notice" role="alert" key={i}>
                  <div className="cd-notice__badge">
                    <Icon name="alert" size={18} />
                    <span>Aviso</span>
                  </div>
                  <p className="cd-notice__text">{obs.Descripcion}</p>
                  <div className="cd-notice__date">
                    Información actualizada el {now.format("DD/MM/YYYY")}
                  </div>
                </div>
              );
            })}

          <div className="cd-grid">
            <aside className="cd-info">
              <span
                className={`cd-kind cd-kind--${isHospital ? "hospital" : "centro"}`}
              >
                <Icon name={isHospital ? "hospital" : "shield"} size={16} />
                {isHospital ? "Hospital" : "Centro asistencial"} {own ? "propio" :"colaborador"}
              </span>
              <h2 className="cd-info__name">
                {(center.Descripcion || "").toUpperCase()}
              </h2>
              <p className="cd-info__addr">{addr}</p>

              <ul className="cd-list">
                {center.Telefono && (
                  <li className="cd-row">
                    <span className="cd-row__icon">
                      <Icon name="phone" size={18} />
                    </span>
                    <div className="cd-row__body">
                      <div className="cd-row__label">Teléfono</div>
                      <a
                        className="cd-row__value cd-row__value--link"
                        href={`tel:${center.Telefono.replace(/\s/g, "")}`}
                      >
                        {center.Telefono}
                      </a>
                    </div>
                  </li>
                )}
                {center.Fax && (
                  <li className="cd-row">
                    <span className="cd-row__icon">
                      <Icon name="printer" size={18} />
                    </span>
                    <div className="cd-row__body">
                      <div className="cd-row__label">Fax</div>
                      <div className="cd-row__value">{center.Fax}</div>
                    </div>
                  </li>
                )}
                {center.Horarios &&
                  center.Horarios.map((shedule: any, i: number) => (
                    <li className="cd-row" key={i}>
                      <span className="cd-row__icon">
                        <Icon
                          name={
                            shedule.TipoHorarioDesc === "Horario Asistencial"
                              ? "clock"
                              : "calendar"
                          }
                          size={18}
                        />
                      </span>
                      <div className="cd-row__body">
                        <div className="cd-row__label">
                          {shedule.TipoHorarioDesc}
                        </div>
                        <div className="cd-row__value">
                          {shedule.DiasSemana}
                        </div>
                        <div className="cd-row__value">
                          {`de ${moment(shedule.FechaValidezInicio).format("H:mm")} a ${moment(shedule.FechaValidezFin).format("H:mm")}`}
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>

              {center.ServiciosOfrecidos?.length > 0 && (
                <div className="cd-services">
                  <div className="cd-services__head">
                    <span className="cd-services__icon">
                      <Icon name="grid" size={18} />
                    </span>
                    <h3 className="cd-services__title">
                      Servicios disponibles
                    </h3>
                  </div>
                  <ul className="cd-services__list">
                    {center.ServiciosOfrecidos.map(
                      (service: any, i: number) => (
                        <li key={i} className="cd-service">
                          <span className="cd-service__dot" aria-hidden></span>
                          {service.Nombre}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </aside>

            <aside className="cd-map" aria-label="Mapa de ubicación">
              {!loadingGetCenter && center.Latitud && center.Longitud && (
                <div className="cd-map__canvas">
                  <MapGoogle2
                    latitudeCenter={center.Latitud}
                    longitudeCenter={center.Longitud}
                    redirect={redirect}
                    title="Centro Solimat"
                    zoom={18}
                  />
                </div>
              )}
              {addr && (
                <div className="cd-map__addr">
                  <Icon name="pin" size={16} />
                  <span>{addr}</span>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CenterDetailNew;
